import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pill, Check, Plus, Pencil, Trash2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import { Drawer, useToast, SkeletonLoader } from "@/components/system";
import {
  PrimaryButton,
  SecondaryButton,
  DialogFooter,
  FormInput,
  FormTextarea,
  browserTz,
} from "@/components/forms";
import { formatError } from "@/lib/format-error";

/**
 * Quick-log supplement (BW7).
 *
 * Lists the user's active stack as a vertical list of "tap-to-take" rows.
 * Tapping inserts a `supplement_intake` row at now() with the configured
 * default dose. The row stamps a check on success so the user can see what
 * they just logged without re-opening the sheet.
 *
 * If no supplements exist yet (e.g. legacy account that completed onboarding
 * before BW7), surface a one-tap "Load my stack" CTA that calls
 * `bootstrap_supplement_seeds()` to populate the canonical six.
 */

export function LogSupplementDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { session } = useSession();
  const userId = session?.user?.id ?? null;
  const qc = useQueryClient();
  const toast = useToast();

  // Today's intakes — used to mark which supplements have already been
  // logged today so the row turns into a "Logged ✓" affordance instead of
  // re-firing on accidental double-tap.
  const todayKey = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }, []);

  const supplementsQ = useQuery({
    queryKey: ["supplements", userId],
    enabled: Boolean(userId) && open,
    staleTime: 60_000,
    queryFn: async () => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from("supplement")
        .select("id, name, brand, dose, unit, form, tier, times_of_day, status")
        .eq("user_id", userId)
        .eq("status", "active")
        .order("name", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });

  const todayIntakesQ = useQuery({
    queryKey: ["supplement_intake_today", userId, todayKey],
    enabled: Boolean(userId) && open,
    staleTime: 30_000,
    queryFn: async () => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from("supplement_intake")
        .select("id, supplement_id, taken_at, dose_taken, dose_unit, notes")
        .eq("user_id", userId)
        .gte("taken_at", todayKey)
        .order("taken_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  // Map supplement_id → most recent intake (today). Used so a logged row can
  // be tapped again to edit/un-log.
  const intakeBySupp = useMemo(() => {
    const m = new Map<string, IntakeRow>();
    for (const it of todayIntakesQ.data ?? []) {
      if (!m.has(it.supplement_id)) m.set(it.supplement_id, it as IntakeRow);
    }
    return m;
  }, [todayIntakesQ.data]);

  const [editingIntake, setEditingIntake] = useState<{
    intake: IntakeRow;
    supplementName: string;
    defaultUnit: string;
  } | null>(null);

  const log = useMutation({
    mutationFn: async (s: { id: string; dose: number | null; unit: string }) => {
      if (!userId) throw new Error("Not signed in");
      const { error } = await supabase.from("supplement_intake").insert({
        user_id: userId,
        supplement_id: s.id,
        taken_at: new Date().toISOString(),
        tz: browserTz(),
        dose_taken: s.dose,
        dose_unit: s.unit,
        source: "quick_log_fab",
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["supplement_intake_today", userId, todayKey],
      });
    },
    onError: (e) => toast.show(`Couldn't log: ${formatError(e)}`),
  });

  const seed = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc("bootstrap_supplement_seeds");
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["supplements", userId] });
      toast.show("Supplement stack loaded.");
    },
    onError: (e) => toast.show(`Couldn't load stack: ${formatError(e)}`),
  });

  const items = supplementsQ.data ?? [];
  const isLoading = supplementsQ.isLoading || todayIntakesQ.isLoading;

  return (
    <Drawer open={open} onClose={onClose} title="Log supplement">
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        {isLoading ? (
          <SkeletonLoader variant="block" count={4} />
        ) : items.length === 0 ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "var(--space-3)",
              padding: "var(--space-4)",
              border: "1px dashed var(--hair-2)",
              borderRadius: "var(--radius-card)",
              fontSize: "var(--type-body-s)",
              color: "var(--fg-2)",
            }}
          >
            <p style={{ margin: 0 }}>
              No active supplements yet. Load Jonas's stack (creatine, B12, D3,
              algae oil, magnesium, tirzepatide) to start tapping.
            </p>
            <PrimaryButton
              onClick={() => seed.mutate()}
              disabled={seed.isPending}
            >
              {seed.isPending ? "Loading…" : "Load my stack"}
            </PrimaryButton>
          </div>
        ) : (
          <ul
            style={{
              listStyle: "none",
              margin: 0,
              padding: 0,
              display: "flex",
              flexDirection: "column",
              gap: "var(--space-2)",
            }}
          >
            {items.map((s) => {
              const intake = intakeBySupp.get(s.id) ?? null;
              const taken = intake != null;
              const dose = s.dose != null ? Number(s.dose) : null;
              return (
                <li key={s.id}>
                  <button
                    type="button"
                    disabled={log.isPending}
                    onClick={() => {
                      if (taken && intake) {
                        // BW12.5 Issue 17: tapping a logged row opens the
                        // edit/un-log drawer instead of being a no-op.
                        setEditingIntake({
                          intake,
                          supplementName: s.name,
                          defaultUnit: s.unit,
                        });
                        return;
                      }
                      log.mutate({ id: s.id, dose, unit: s.unit });
                    }}
                    aria-label={
                      taken ? `Edit ${s.name} log` : `Log ${s.name}`
                    }
                    style={{
                      display: "grid",
                      gridTemplateColumns: "32px 1fr max-content",
                      alignItems: "center",
                      gap: "var(--space-3)",
                      width: "100%",
                      padding: "var(--space-3) var(--space-3)",
                      background: taken ? "var(--accent-wash)" : "transparent",
                      color: "var(--fg)",
                      border: `1px solid ${taken ? "var(--accent-dim)" : "var(--hair)"}`,
                      borderRadius: "var(--radius-card)",
                      cursor: "pointer",
                      textAlign: "left",
                      minHeight: 56,
                    }}
                  >
                    <span
                      aria-hidden
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: "var(--radius-card)",
                        background: taken ? "var(--accent)" : "var(--bg-3)",
                        color: taken ? "#0a0b0d" : "var(--fg-3)",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {taken ? (
                        <Check size={18} strokeWidth={2} />
                      ) : (
                        <Pill size={18} strokeWidth={1.5} />
                      )}
                    </span>
                    <span
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        minWidth: 0,
                      }}
                    >
                      <span style={{ color: "var(--fg)", fontWeight: 500 }}>
                        {s.name}
                      </span>
                      <span
                        style={{
                          color: "var(--fg-3)",
                          fontSize: "var(--type-meta)",
                        }}
                      >
                        {taken && intake?.dose_taken != null
                          ? `${intake.dose_taken} ${intake.dose_unit ?? s.unit}`
                          : dose != null
                            ? `${dose} ${s.unit}`
                            : s.unit}
                        {s.tier ? ` · ${s.tier}` : null}
                      </span>
                    </span>
                    <span
                      className="mono"
                      style={{
                        fontSize: "var(--type-meta)",
                        color: taken ? "var(--accent)" : "var(--fg-3)",
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      {taken ? (
                        <>
                          <Pencil size={11} strokeWidth={1.6} aria-hidden />
                          Logged
                        </>
                      ) : (
                        "Tap to log"
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <DialogFooter>
          <SecondaryButton onClick={onClose}>Done</SecondaryButton>
          {items.length > 0 ? (
            <span
              style={{
                color: "var(--fg-3)",
                fontSize: "var(--type-meta)",
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <Plus size={12} strokeWidth={1.5} aria-hidden /> Add new (BW8)
            </span>
          ) : null}
        </DialogFooter>
      </div>

      {editingIntake && (
        <EditIntakeDrawer
          intake={editingIntake.intake}
          supplementName={editingIntake.supplementName}
          defaultUnit={editingIntake.defaultUnit}
          onClose={() => setEditingIntake(null)}
          onSaved={() => {
            void qc.invalidateQueries({
              queryKey: ["supplement_intake_today", userId, todayKey],
            });
            setEditingIntake(null);
          }}
        />
      )}
    </Drawer>
  );
}

type IntakeRow = {
  id: string;
  supplement_id: string;
  taken_at: string;
  dose_taken: number | null;
  dose_unit: string | null;
  notes: string | null;
};

function EditIntakeDrawer({
  intake,
  supplementName,
  defaultUnit,
  onClose,
  onSaved,
}: {
  intake: IntakeRow;
  supplementName: string;
  defaultUnit: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [dose, setDose] = useState(
    intake.dose_taken != null ? String(intake.dose_taken) : "",
  );
  const [unit, setUnit] = useState(intake.dose_unit ?? defaultUnit);
  const [notes, setNotes] = useState(intake.notes ?? "");
  const [takenAt, setTakenAt] = useState(toLocalDt(intake.taken_at));

  const save = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("supplement_intake")
        .update({
          dose_taken: dose ? Number(dose) : null,
          dose_unit: unit || null,
          notes: notes.trim() || undefined,
          taken_at: new Date(takenAt).toISOString(),
        })
        .eq("id", intake.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.show(`${supplementName} updated.`);
      onSaved();
    },
    onError: (e) => toast.show(`Couldn't save: ${formatError(e)}`),
  });

  const unlog = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("supplement_intake")
        .delete()
        .eq("id", intake.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.show(`${supplementName} un-logged.`);
      onSaved();
    },
    onError: (e) => toast.show(`Couldn't un-log: ${formatError(e)}`),
  });

  return (
    <Drawer open onClose={onClose} title={`Edit ${supplementName}`} size="sm">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
        style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "var(--space-3)",
          }}
        >
          <FormInput
            label="Dose taken"
            inputMode="decimal"
            value={dose}
            onChange={setDose}
            placeholder="500"
          />
          <FormInput label="Unit" value={unit} onChange={setUnit} />
        </div>
        <FormInput
          label="Logged at"
          type="datetime-local"
          value={takenAt}
          onChange={setTakenAt}
        />
        <FormTextarea
          label="Notes"
          value={notes}
          onChange={setNotes}
          rows={3}
          placeholder="Took with breakfast · half-dose due to side effects · …"
        />
        <DialogFooter>
          <button
            type="button"
            onClick={() => {
              if (confirm("Un-log this intake? It removes the row entirely.")) {
                unlog.mutate();
              }
            }}
            disabled={unlog.isPending || save.isPending}
            style={{
              height: 38,
              padding: "0 var(--space-4)",
              background: "transparent",
              color: "var(--brick)",
              border: "1px solid var(--hair-2)",
              borderRadius: "var(--radius-card)",
              cursor: "pointer",
              marginRight: "auto",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: "var(--type-body-s)",
            }}
          >
            <Trash2 size={14} strokeWidth={1.5} />
            Un-log
          </button>
          <SecondaryButton onClick={onClose} disabled={save.isPending}>
            Cancel
          </SecondaryButton>
          <PrimaryButton type="submit" disabled={save.isPending}>
            {save.isPending ? "Saving…" : "Save"}
          </PrimaryButton>
        </DialogFooter>
      </form>
    </Drawer>
  );
}

function toLocalDt(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}
